import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { calculateValue, getNumberValue, getValidatedValue } from "./number-input-utils";

const baseClass = "ux-number-input";
let id = 0;

@Component({
    tag: "ux-number-input",
    styleUrl: "number-input.scss",
    shadow: false
})
export class NumberInput {
    private inputElement: HTMLInputElement;
    /** name attribute for the input */
    @Prop() readonly name?: string = "";
    /** initial value for the input */
    @Prop() readonly value?: string;
    /** @deprecated use value instead */
    @Prop() readonly initialValue?: string;
    /** size of increment/decrement for the plus/minus button */
    @Prop() readonly step?: string = "1";
    /** max value */
    @Prop() readonly max?: string = "";
    /** min value */
    @Prop() readonly min?: string = "";
    /** Label text describing the number input field */
    @Prop() readonly label: string = "";
    /** additional info label for the the input field */
    @Prop() readonly labelInfo: string = "";
    /** indicates if the number input is disabled */
    @Prop() readonly disabled: boolean = false;
    /** indicates if the number input is required  */
    @Prop() readonly required: boolean = false;
    /** indicates if the number input has an error state  */
    @Prop() readonly error: boolean = false;
    /** Error message that is rendered if component has an error */
    @Prop() readonly errorMsg: string | null = null;
    /** id attribute for the input element, this will be set automatically if it is not set by the developer */
    /* eslint-disable-next-line */
    @Prop() inputId: string = "";
    /** indicates if the component should occupy the full container width  */
    @Prop() readonly fullWidth: boolean = false;
    /** indicates if the minus button is disabled when reaching the min value  */
    @Prop() readonly disabledMinusButton: boolean = false;

    @Element() domNode!: HTMLUxNumberInputElement;

    @State() currentValue: number;
    @State() minValue: number;
    @State() maxValue: number;
    @State() stepValue: number;

    componentWillLoad() {
        // @ts-expect-error Check for typing
        const initialValue = !isNaN(getNumberValue(this.initialValue)) ? this.initialValue : this.value;
        // @ts-expect-error Check for typing
        const initialNumber = +initialValue;
        this.currentValue = !isNaN(initialNumber) ? initialNumber : 0;
        this.setNumbers();
        if (this.inputId === "") {
            this.inputId = `${baseClass}-id-${id++}`;
        }
    }
    /** Emitted when the value has changed. */
    @Event({
        eventName: "uxNumberInputChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    numberInputChange!: EventEmitter;

    @Watch("min")
    @Watch("max")
    @Watch("step")
    private setNumbers() {
        const { max, min, step } = this;
        // @ts-expect-error Check for typing
        this.minValue = getNumberValue(min);
        // @ts-expect-error Check for typing
        this.maxValue = getNumberValue(max);
        // @ts-expect-error Check for typing
        const stepValue = getNumberValue(step);
        this.stepValue = isNaN(stepValue) ? 1 : stepValue;
    }

    private handlePlus() {
        this.changeValue("increment");
    }

    private handleMinus() {
        this.changeValue("decrement");
    }

    private changeValue(type: string = "increment") {
        const { stepValue, maxValue, minValue, currentValue } = this;
        this.currentValue = calculateValue(type, currentValue, stepValue, maxValue, minValue);
        this.inputElement.value = this.currentValue.toString();

        this.numberInputChange.emit({
            currentValue: this.currentValue,
            inputName: this.name,
            inputId: this.inputId
        });
    }

    private onChange(event: Event) {
        const { maxValue, minValue, currentValue } = this;
        const { value } = event.target as HTMLInputElement;
        this.currentValue = getValidatedValue(value, currentValue, maxValue, minValue);
        this.inputElement.value = this.currentValue.toString();

        this.numberInputChange.emit({
            currentValue: this.currentValue,
            inputName: this.name,
            inputId: this.inputId
        });
    }

    private minusButtonDisabled() {
        return this.disabledMinusButton && this.minValue === this.currentValue;
    }

    render() {
        const {
            name,
            max,
            min,
            stepValue,
            currentValue,
            label,
            labelInfo,
            disabled,
            required,
            error,
            errorMsg,
            inputId,
            fullWidth
        } = this;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--error`]: error,
                    [`${baseClass}--full-width`]: fullWidth
                }}
            >
                <ux-input-label
                    text={label}
                    labelClasses={`${baseClass}__label`}
                    inputId={inputId}
                    error={error}
                    // @ts-expect-error Check for typing
                    errorMsg={errorMsg}
                    required={required}
                    labelInfo={labelInfo}
                >
                    <slot />
                </ux-input-label>
                <div>
                    <div class={`${baseClass}__wrapper`}>
                        <button
                            type="button"
                            class={{
                                [`${baseClass}__btn`]: true,
                                [`${baseClass}__btn--disabled`]: this.minusButtonDisabled()
                            }}
                            onClick={() => this.handleMinus()}
                        >
                            –
                        </button>
                        <input
                            class={`${baseClass}__input`}
                            name={name}
                            step={stepValue.toString()}
                            type="number"
                            id={inputId}
                            max={max}
                            min={min}
                            value={currentValue}
                            // @ts-expect-error Check for typing
                            ref={(el) => (this.inputElement = el)}
                            onInput={(event) => this.onChange(event)}
                            aria-invalid={error ? "true" : "false"}
                        />
                        <button type="button" class={`${baseClass}__btn`} onClick={() => this.handlePlus()}>
                            +
                        </button>
                    </div>
                    <div class={`${baseClass}__slot`}>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
